import { NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import React, { useCallback, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { ColorPicker } from '../../../common/color-picker/ColorPicker';
import { RfNodeStickyNote, SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { RichTextEditorProvider } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { RichTextEditor } from '../../../common/rich-text-editor/RichTextEditor';
import { RichTextToolbar } from '../../../common/rich-text-editor/RichTextToolbar';
import { SmallColorSwatchButton } from '../../../common/color-picker/SmallColorSwatchButton';
import { CanvasNodeToolbar } from '../components/CanvasNodeToolbar';
import { StickyNodeDuplicators } from './StickyNodeDuplicators';
import { NodeActionsMenu } from '../components/NodeActionsMenu';
import { useNodeRichTextEditBehavior } from '../hooks/useNodeRichTextEditBehavior';
import { useNodeDeleteOnKeyPress } from '../hooks/useNodeDeleteOnKeyPress';
import { useNodeResizing } from '../hooks/useNodeResizing';
import { Separator } from '../../../common/rich-text-editor/toolbar-items/Separator';
import { useRichTextEditor } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { CSS_Z_INDEX_NODE_HANDLE } from '../../const/zIndex';

const STICKY_NOTE_DEFAULT_COLOR = '#EBDEFF';

const PLACEHOLDER_TEXT = 'Type anything';

const styles = {
  leftToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    alignItems: 'top',
    justifyContent: 'top',
    backgroundColor: 'white',
    padding: '8px',
    border: `1px solid ${colors.grey[300]}`,
    '&.drop-target': {
      borderColor: colors.blue[500],
    },
    borderRadius: 0,
  },
  handle: {
    width: 12,
    height: 12,
    zIndex: CSS_Z_INDEX_NODE_HANDLE,
  },
  lineStyle: {
    borderColor: colors.blue[500],
    borderWidth: 1,
  },
} as const;

const STICKY_NOTE_MIN_WIDTH = 120;
const STICKY_NOTE_MIN_HEIGHT = 120;

const StickyNoteNode = ({
  id,
  selected,
  dragging,
  data: { color, richText, temporary, freshlyCreated },
}: NodeProps<RfNodeStickyNote>) => {
  const { updateNodeData } = useReactFlow<SweepCanvasRfNode>();

  const [popoverAnchor, setPopoverAnchor] = useState<(EventTarget & Element) | null>(null);
  const [selectedColor, setSelectedColor] = useState(color ?? STICKY_NOTE_DEFAULT_COLOR);

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
  }, []);

  const handleColorChange = useCallback(
    (newColor: string) => {
      setSelectedColor(newColor);
      updateNodeData(id, { color: newColor });
      handleClosePopover();
    },
    [id, updateNodeData, handleClosePopover],
  );

  const {
    editMode,
    exclusivelySelected,
    setEditMode,
    multipleNodesSelected,
    handleRichTextChange,
    saveRichText,
  } = useNodeRichTextEditBehavior({
    id,
    freshlyCreated,
    selected,
    dragging,
    temporary,
    currentRichText: richText,
  });

  useNodeDeleteOnKeyPress({
    nodeId: id,
    exclusivelySelected,
    editMode,
  });

  const { urlEditorOpen } = useRichTextEditor();

  const { resizing, setResizeTrue, setResizeFalse } = useNodeResizing();

  const maybeSetEditMode = useCallback(() => {
    if (exclusivelySelected) {
      setEditMode(true);
    }
  }, [exclusivelySelected, setEditMode]);

  const placeholder = temporary || !exclusivelySelected ? undefined : PLACEHOLDER_TEXT;

  return (
    <>
      {!temporary && !editMode && (
        <NodeResizer
          isVisible={selected && !dragging && !multipleNodesSelected}
          handleClassName="section-node-handle"
          handleStyle={styles.handle}
          lineStyle={styles.lineStyle}
          minWidth={STICKY_NOTE_MIN_WIDTH}
          minHeight={STICKY_NOTE_MIN_HEIGHT}
          autoScale
          onResizeStart={setResizeTrue}
          onResize={setResizeTrue}
          onResizeEnd={setResizeFalse}
        />
      )}

      {!temporary && !resizing && (
        <CanvasNodeToolbar
          className="nopan"
          align="center"
          isVisible={exclusivelySelected}
          offset={18}
          hide={urlEditorOpen}
        >
          <SmallColorSwatchButton
            color={selectedColor}
            onClick={handleButtonClick}
            pressed={Boolean(popoverAnchor)}
          />
          <Separator />
          <RichTextToolbar />
          <Separator />
          <NodeActionsMenu nodeId={id} />
        </CanvasNodeToolbar>
      )}
      {exclusivelySelected && !resizing && <StickyNodeDuplicators nodeId={id} />}
      <Paper
        sx={{
          ...styles.outerContainer,
          backgroundColor: `${selectedColor}`,
          opacity: temporary ? 0.5 : 1,
          border: editMode ? `1px solid ${colors.blue[500]}` : `1px solid ${selectedColor}`,
          cursor: 'move',
        }}
        elevation={temporary ? 0 : 1}
        className={!editMode ? 'drag-handle' : undefined}
        onClick={maybeSetEditMode}
      >
        <Box sx={{ width: '100%', height: '100%', cursor: 'text', position: 'relative' }}>
          <RichTextEditor
            onChange={handleRichTextChange}
            onBlur={saveRichText}
            initialContent={richText}
            placeholder={placeholder}
            autoFocus
            hideOutline
            editable={editMode}
          />
          {!editMode && (
            <Box
              className="drag-handle"
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: CSS_Z_INDEX_NODE_HANDLE,
                cursor: 'move',
              }}
              onClick={() => {
                if (exclusivelySelected) {
                  setEditMode(true);
                }
              }}
            />
          )}
        </Box>
      </Paper>

      {popoverAnchor && (
        <ColorPicker
          anchorElement={popoverAnchor}
          closePopover={handleClosePopover}
          onColorChange={handleColorChange}
          selectedColor={selectedColor}
          singleRow
          position="top"
        />
      )}
    </>
  );
};

const StickyNoteNodeWithProviders = (props: NodeProps<RfNodeStickyNote>) => {
  return (
    <RichTextEditorProvider>
      <StickyNoteNode {...props} />
    </RichTextEditorProvider>
  );
};

export default StickyNoteNodeWithProviders;
